import Icon from "../../components/ui/Icon";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import FormFieldsContainer from "../../components/ui/FormFieldsContainer";
import Header from "../../components/ui/Header";
import useImageUploader from "../../components/ui/ImageUploader";
import VisibilityScope, { VisibilityScopeValue } from "../../components/ui/VisibilityScope";
import {
  Button,
  Card,
  FlexContent,
  Form,
  FormField,
  Input,
  Select,
  SettingsIcon,
  Textarea,
  TranslatableField,
  useDebounce,
} from "@rothoblaas/design-system";
import { translatableFieldProps } from "../../utils/translatableField";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { CategoryLight } from "../../http/types/categories";
import { FullModule, UpsertModuleNewImageBody, UpsertModuleRemoveImageBody } from "../../http/types/modules";
import { useCategoriesApi } from "../../http/useCategories";
import { useModulesApi } from "../../http/useModules";
import { getAllTypes, PaginationQuery } from "../../types";

export const NewAdminModule: FC = () => {
  const i18n = useI18n();
  const { permissions } = useAuthContext();

  return (
    <>
      <Header
        title={i18n.translation.modules.createNew}
        path={[
          { display: i18n.translation.modules.all, url: "/admin/modules" },
          { display: i18n.translation.modules.createNew },
        ]}
      />

      <AdminModuleForm disabled={!permissions.admin.canManageModules} />
    </>
  );
};

export const EditAdminModule: FC = () => {
  const i18n = useI18n();
  const { moduleId } = useParams<{ moduleId: string }>();
  const modulesApi = useModulesApi();
  const { permissions } = useAuthContext();
  const navigate = useNavigate();

  const aborter = useRef(new AbortController());

  const moduleSwr = modulesApi.getAdminModule(moduleId!, aborter.current.signal);

  useEffect(() => {
    moduleId && moduleSwr.trigger();
  }, [moduleId]);

  if (!moduleSwr.data && !moduleSwr.error) {
    return null;
  }

  return (
    <>
      <Header
        title={moduleSwr.data?.name ?? i18n.translation.common.loading}
        error={moduleSwr.error}
        accentColor={moduleSwr.data?.category?.color}
        path={[
          { display: i18n.translation.modules.all, url: "/admin/modules" },
          { display: moduleSwr.data?.name ?? "" },
        ]}
        buttons={
          <Button onClick={() => navigate(`/admin/modules/${moduleId}/settings`)}><Icon glyph={SettingsIcon} />
            {i18n.translation.common.settings}
          </Button>
        }
      />

      <AdminModuleForm
        module={!moduleSwr.error ? moduleSwr.data : undefined}
        disabled={moduleSwr.isMutating || !!moduleSwr.error || !permissions.admin.canManageModules}
        isLoading={moduleSwr.isMutating}
      />
    </>
  );
};

interface AdminModuleFormBody {
  name: string | null;
  description: string | null;
  type: string | null;
  categoryId: string | null;
  visibility: VisibilityScopeValue;

  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;

  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
}

interface NewEditAdminModuleProps {
  module?: FullModule;
  disabled?: boolean;
  isLoading?: boolean;
}

const AdminModuleForm: FC<NewEditAdminModuleProps> = ({ module, disabled, isLoading }: NewEditAdminModuleProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const modulesApi = useModulesApi();
  const api = useApi();

  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const categories = useCategoriesApi().useAdminCategories(query, aborter.current.signal).data?.items;
  const allTypes = getAllTypes();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues: AdminModuleFormBody = {
    name: module?.name ?? null,
    nameIt: module?.nameIt ?? null,
    nameDe: module?.nameDe ?? null,
    nameEs: module?.nameEs ?? null,
    nameFr: module?.nameFr ?? null,
    description: module?.description ?? null,
    descriptionIt: module?.descriptionIt ?? null,
    descriptionDe: module?.descriptionDe ?? null,
    descriptionEs: module?.descriptionEs ?? null,
    descriptionFr: module?.descriptionFr ?? null,
    type: module?.type ?? null,
    categoryId: module?.categoryId ?? null,
    visibility: module?.visibility ?? "hidden",
  };

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: module?.image,
    isLoading: false,
    disabled: disabled ?? false,
  });

  const submit = (body: UpsertModuleRemoveImageBody | UpsertModuleNewImageBody) => {
    if (!!module) {
      modulesApi
        .updateAdminModule(module.id, body)
        .then(() => {
          setIsSubmitting(false);
          onSave();
          dispatch(showSuccessNotification(i18n.translation));
        })
        .catch((err) => {
          dispatch(showErrorNotification(i18n, err));
          setIsSubmitting(false);
        })
        .finally(() => setIsSubmitting(false));
    }
  };

  const onSubmitForm = () => (formData: AdminModuleFormBody) => {
    setIsSubmitting(true);

    if (newImage) {
      modulesApi
        .generateUploadImageUrl(module?.id!, newImage.name, newImage.type)
        .then((signedUrl) => {
          api.uploadFile(signedUrl, newImage).then((res) => {
            if (!res.ok) {
              dispatch(showErrorNotification(i18n, res));
              return;
            }

            const body: UpsertModuleNewImageBody = {
              name: formData.name ?? "",
              description: formData.description ?? "",
              type: formData.type ?? "",
              categoryId: formData.categoryId ?? "",
              image: newImage.name,
              visibility: formData.visibility,

              nameIt: formData.nameIt ?? "",
              nameDe: formData.nameDe ?? "",
              nameEs: formData.nameEs ?? "",
              nameFr: formData.nameFr ?? "",

              descriptionIt: formData.descriptionIt ?? "",
              descriptionDe: formData.descriptionDe ?? "",
              descriptionEs: formData.descriptionEs ?? "",
              descriptionFr: formData.descriptionFr ?? "",
            };

            submit(body);
          });
        })
        .catch((err) => {
          dispatch(showErrorNotification(i18n, err));
          setIsSubmitting(false);
        })
        .finally(() => setIsSubmitting(false));
    } else {
      const body: UpsertModuleRemoveImageBody = {
        name: formData.name ?? "",
        description: formData.description ?? "",
        type: formData.type ?? "",
        categoryId: formData.categoryId ?? "",
        removeImage: removeImage,
        visibility: formData.visibility,

        nameIt: formData.nameIt ?? "",
        nameDe: formData.nameDe ?? "",
        nameEs: formData.nameEs ?? "",
        nameFr: formData.nameFr ?? "",

        descriptionIt: formData.descriptionIt ?? "",
        descriptionDe: formData.descriptionDe ?? "",
        descriptionEs: formData.descriptionEs ?? "",
        descriptionFr: formData.descriptionFr ?? "",
      };
      submit(body);
    }
  };

  return (
    <FlexContent padding noPaddingTop direction="row">
      <Card shadow>
        <Form<AdminModuleFormBody>
        submitText={i18n.translation.common.save}
        disabled={disabled || isSubmitting}
        defaultValues={defaultValues}
        onSubmit={() => onSubmitForm()}
      >
        {({ register, formState: { errors }, reset, control, watch, setValue }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [module]);

          useEffect(() => {
            register("nameIt");
            register("nameDe");
            register("nameEs");
            register("nameFr");
            register("descriptionIt");
            register("descriptionDe");
            register("descriptionEs");
            register("descriptionFr");
          }, [register]);

          const values = watch();

          return (
            <FormFieldsContainer imageUploader={component}>
              <FormField label={i18n.translation.common.name} error={errors.name} isOptional={false}>
                {({ labelId, isOptional, isInvalid }) => (
                  <TranslatableField
                    {...translatableFieldProps("name", values, setValue, i18n.translation)}
                    dialogTitle={i18n.translation.common.name}
                  >
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled || isLoading}
                      {...register("name", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                )}
              </FormField>

              <FormField label={i18n.translation.common.description} error={errors.description} isOptional={false}>
                {({ labelId, isOptional, isInvalid }) => (
                  <TranslatableField
                    {...translatableFieldProps("description", values, setValue, i18n.translation)}
                    dialogTitle={i18n.translation.common.description}
                    type="textarea"
                  >
                    <Textarea
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled || isLoading}
                      {...register("description", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                )}
              </FormField>

              {/* TYPE LIST */}
              <Controller
                name="type"
                control={control}
                rules={{ required: true }}
                defaultValue={defaultValues.type}
                render={({ field: { onChange, value, ref, ...fieldProps }, fieldState }) => (
                  <FormField label={i18n.translation.common.type} error={fieldState.error} isOptional={false}>
                    {({ labelId }) => (
                      <Select
                        {...fieldProps}
                        inputId={labelId}
                        options={allTypes}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        value={allTypes.find((t) => t.value === value) || null}
                        onChange={(option: { label: string; value: string } | null) =>
                          onChange(option ? option.value : null)
                        }
                        placeholder={`${i18n.translation.common.select}...`}
                        isDisabled={false}
                        isInvalid={fieldState.invalid}
                      />
                    )}
                  </FormField>
                )}
              />

              {/* CATEGORY */}
              <Controller
                name="categoryId"
                control={control}
                rules={{ required: true }}
                defaultValue={defaultValues.categoryId}
                render={({ field: { onChange, value, ref, ...fieldProps }, fieldState }) => (
                  <FormField label={i18n.translation.common.category} error={fieldState.error}>
                    {({ labelId }) => (
                      <Select
                        {...fieldProps}
                        inputId={labelId}
                        options={categories}
                        getOptionLabel={(x: CategoryLight) => x.name}
                        getOptionValue={(x: CategoryLight) => x.id}
                        value={categories?.find((c: CategoryLight) => c.id === value) || null}
                        onChange={(option: CategoryLight | null) => onChange(option ? option.id : null)}
                        placeholder={`${i18n.translation.common.select}...`}
                        isDisabled={false}
                        isInvalid={fieldState.invalid}
                      />
                    )}
                  </FormField>
                )}
              />

              {/* VISIBILITY */}
              <FormField label={i18n.translation.common.visibility}>
                {({ labelId }) => (
                  <VisibilityScope
                    inputId={labelId}
                    value={watch("visibility")}
                    onChange={(value) => setValue("visibility", value)}
                    isDisabled={false}
                  />
                )}
              </FormField>
            </FormFieldsContainer>
          );
        }}
      </Form>
      </Card>
    </FlexContent>
  );
};
